import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Brain, Search, CheckCircle2 } from 'lucide-react';

import { useLanguage } from '../context/LanguageContext';
/**
 * AiMatchingLoader
 * ---------------------------------------------------------
 * A WOW-moment fullscreen overlay that appears right before
 * navigating to instant-lesson search. Gives users the feeling
 * of an AI actively analysing tutors to find their best match.
 */
const STEPS_AR = [
  { label: 'نحلّل ملفات أكثر من 50 معلم متاح الآن…', icon: Brain },
  { label: 'نقارن التخصصات، التقييمات، وأوقات التوفّر…', icon: Search },
  { label: 'نرتّب أفضل تطابق لك بالذكاء الاصطناعي…', icon: Sparkles },
  { label: 'تم! جاري فتح المعلمين المناسبين لك.', icon: CheckCircle2 },
];

const STEPS_EN = [
  { label: 'Analysing 50+ live tutor profiles…', icon: Brain },
  { label: 'Matching subjects, ratings and availability…', icon: Search },
  { label: 'Ranking your best-fit tutors with AI…', icon: Sparkles },
  { label: 'Done! Opening your personal matches.', icon: CheckCircle2 },
];

const AiMatchingLoader = ({ open, onFinish, language = 'ar' }) => {
  const { localize } = useLanguage();
  const isAr = language === 'ar';
  const steps = isAr ? STEPS_AR : STEPS_EN;
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!open) { setIdx(0); return; }
    const timers = [];
    steps.forEach((_, i) => {
      timers.push(setTimeout(() => setIdx(i), i * 650));
    });
    const doneTimer = setTimeout(() => onFinish?.(), steps.length * 650 + 350);
    timers.push(doneTimer);
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            background:
              'radial-gradient(circle at 20% 20%, rgba(16,185,129,0.25), transparent 40%),' +
              'radial-gradient(circle at 85% 80%, rgba(245,158,11,0.22), transparent 40%),' +
              'rgba(10, 17, 27, 0.88)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
          }}
          data-testid="ai-matching-loader"
          dir={isAr ? 'rtl' : 'ltr'}
        >
          {/* Orbit rings around the AI core */}
          <div className="relative">
            {/* Outer pulsing rings */}
            {[0, 0.4, 0.8].map((d, i) => (
              <motion.div
                key={i}
                className="absolute inset-0 rounded-full border border-emerald-400/40"
                initial={{ scale: 0.8, opacity: 0.6 }}
                animate={{ scale: 2.4, opacity: 0 }}
                transition={{ duration: 2.2, delay: d, repeat: Infinity, ease: 'easeOut' }}
                style={{ width: 260, height: 260, left: -130, top: -130 }}
              />
            ))}

            {/* AI core */}
            <motion.div
              className="relative w-[130px] h-[130px] rounded-full flex items-center justify-center shadow-2xl"
              style={{
                background:
                  'conic-gradient(from 0deg, #10b981, #f59e0b, #10b981)',
              }}
              animate={{ rotate: 360 }}
              transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
            >
              <div className="absolute inset-[4px] rounded-full bg-[#0a111b] flex items-center justify-center">
                <motion.div
                  animate={{ scale: [1, 1.12, 1] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                  className="relative"
                >
                  <Brain className="w-14 h-14 text-emerald-400" />
                  <motion.div
                    className="absolute -top-1 -right-1"
                    animate={{ opacity: [0.4, 1, 0.4], scale: [0.8, 1.2, 0.8] }}
                    transition={{ duration: 1.8, repeat: Infinity }}
                  >
                    <Sparkles className="w-5 h-5 text-amber-300" />
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Caption stack */}
          <div className="absolute inset-x-0 bottom-[18%] flex flex-col items-center gap-5 px-6">
            <motion.p
              className="text-white/90 text-sm uppercase tracking-[0.35em] font-semibold"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              {localize({ ar: 'الذكاء الاصطناعي يعمل', en: 'AI AT WORK' })}
            </motion.p>

            <div className="min-h-[60px] flex items-center justify-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.35 }}
                  className="flex items-center gap-3 text-white text-base md:text-lg font-medium"
                >
                  {(() => {
                    const I = steps[idx].icon;
                    return <I className="w-5 h-5 text-amber-300 flex-shrink-0" />;
                  })()}
                  <span className="text-center">{steps[idx].label}</span>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Progress dots */}
            <div className="flex items-center gap-2">
              {steps.map((_, i) => (
                <motion.div
                  key={i}
                  className="h-1.5 rounded-full"
                  animate={{
                    width: i <= idx ? 28 : 10,
                    backgroundColor: i <= idx ? '#10b981' : 'rgba(255,255,255,0.25)',
                  }}
                  transition={{ duration: 0.4 }}
                />
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AiMatchingLoader;
